import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle } from "react-icons/fa";
import CardForm from "../components/CardForm";
import Loading from "../components/Loading";
import StripeProvider from "../components/StripeProvider";
import useSubscriptionCheckout, {
  SUBSCRIPTION_FEATURES,
} from "../hooks/useSubscriptionCheckout";

const Payment = () => {
  const navigate = useNavigate();
  const { theme } = useSelector((state) => state.theme);
  const isDark = theme === "dark";

  const {
    user,
    program,
    programLoading,
    selectedPlan,
    setSelectedPlan,
    selectedGateway,
    setSelectedGateway,
    loading,
    clientSecret,
    showModal,
    activating,
    hasActiveAccess,
    enrollNow,
    onCardConfirmed,
    closeCardModal,
  } = useSubscriptionCheckout({
    successPath: "/courses",
    signinRedirect: "/signin?redirect=/payment",
  });

  const cardClass = isDark
    ? "bg-[#2f3349] border-gray-700 text-[#cfd3ec]"
    : "bg-white border-gray-200 text-gray-800";

  const gatewayBtn = (active) =>
    `border-2 rounded-full px-4 py-2 cursor-pointer text-sm font-semibold transition ${
      active
        ? "border-indigo-600 bg-indigo-600 text-white"
        : isDark
          ? "border-gray-500 text-gray-200 hover:border-indigo-400"
          : "border-gray-300 text-gray-700 hover:border-indigo-400"
    }`;

  if (programLoading) {
    return (
      <div className="flex-1 flex items-center justify-center h-full min-h-0">
        <Loading message="Loading subscription..." />
      </div>
    );
  }

  return (
    <div className="relative flex-1 overflow-y-auto p-4 sm:p-8">
      {(loading || activating) && (
        <div
          className={`absolute inset-0 z-20 flex items-center justify-center ${
            isDark ? "bg-[#232634]/80" : "bg-white/80"
          }`}
        >
          <Loading
            message={
              activating ? "Activating your subscription..." : "Starting checkout..."
            }
          />
        </div>
      )}

      <div className="mx-auto max-w-5xl">
        <h1 className={`text-2xl sm:text-3xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
          Subscribe
        </h1>
        <p className={`mt-2 max-w-2xl text-sm sm:text-base ${isDark ? "text-gray-400" : "text-gray-600"}`}>
          Unlock courses, alerts, chat, and market data. Free 7-day trial, then billed according
          to the plan you choose. Cancel anytime.
        </p>

        <div className="mt-8 flex flex-col lg:flex-row items-start justify-center gap-8">
          <div className={`w-full lg:w-[420px] rounded-2xl border p-6 sm:p-8 shadow-lg ${cardClass}`}>
            <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              {program?.name || "Options Mentorship"}
            </h2>
            <p className="text-indigo-400 text-sm mt-1">Join now for immediate access</p>
            <p className={`text-sm mt-1 mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
              Free 7-day trial then
            </p>

            <div className="mb-4">
              <label className={`block mb-1 text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Select Plan
              </label>
              <select
                value={selectedPlan}
                onChange={(e) => setSelectedPlan(e.target.value)}
                className={`w-full px-4 py-2 mt-1 rounded border focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                  isDark
                    ? "bg-gray-700 text-white border-gray-600"
                    : "bg-white text-gray-900 border-gray-300"
                }`}
              >
                <option value={1}>
                  ${program?.monthly_price ?? "—"} (1 Month)
                </option>
                <option value={2}>
                  ${program?.yearly_price ?? "—"} (1 Year)
                </option>
              </select>
              <p className="text-sm text-red-400 mt-1">Cancel anytime</p>
            </div>

            {!hasActiveAccess ? (
              <>
                <div className="flex items-center justify-evenly mb-4">
                  <button
                    type="button"
                    className={gatewayBtn(selectedGateway === "stripe")}
                    onClick={() => setSelectedGateway("stripe")}
                  >
                    Stripe
                  </button>
                  <button
                    type="button"
                    className={gatewayBtn(selectedGateway === "paypal")}
                    onClick={() => setSelectedGateway("paypal")}
                  >
                    PayPal
                  </button>
                </div>

                {activating ? (
                  <div
                    className={`w-full py-3 rounded-full font-medium mb-3 text-center ${
                      isDark ? "bg-gray-700 text-white" : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    Activating your subscription…
                  </div>
                ) : !clientSecret ? (
                  <button
                    type="button"
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-full font-semibold mb-3 disabled:opacity-60"
                    onClick={enrollNow}
                    disabled={loading || !program?.id}
                  >
                    {loading ? "Starting checkout…" : "Subscribe now"}
                  </button>
                ) : (
                  <div className="mb-4">
                    <StripeProvider>
                      <CardForm
                        isOpen={showModal}
                        onClose={closeCardModal}
                        clientSecret={clientSecret}
                        onComplete={onCardConfirmed}
                        billingName={[user?.first_name, user?.last_name]
                          .filter(Boolean)
                          .join(" ")}
                      />
                    </StripeProvider>
                  </div>
                )}

                <p className={`text-sm mt-2 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  Act fast, seats are limited!
                </p>
              </>
            ) : (
              <>
                <p className={`text-sm mb-4 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  You are already subscribed.
                </p>
                <button
                  type="button"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-full font-medium"
                  onClick={() => navigate("/dashboard")}
                >
                  Go to dashboard
                </button>
              </>
            )}
          </div>

          <div className="space-y-4 text-left pt-2">
            {SUBSCRIPTION_FEATURES.map((item) => (
              <div
                key={item}
                className={`flex items-center gap-3 ${isDark ? "text-[#cfd3ec]" : "text-gray-800"}`}
              >
                <FaCheckCircle className="text-indigo-400 shrink-0" />
                <span className="text-base">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
